import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { ArrowRight, MapPin, Search, Filter } from "lucide-react";
import { useCountry } from "@/lib/country";

export function TreatmentsList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (cat) params.category = cat;
    http.get("/treatments", { params }).then(r => setItems(r.data));
  }, [q, cat]);
  const categories = [...new Set(items.map(i => i.category))];
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="overline mb-3">Treatment Directory</div>
      <h1 className="font-serif text-4xl md:text-6xl text-[#F3E9EC]">Explore treatments in India</h1>
      <div className="mt-8 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B47A9A]"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search treatments..." className="mp-input pl-11" data-testid="treatments-search"/>
        </div>
        <select value={cat} onChange={e=>setCat(e.target.value)} className="mp-select md:w-64" data-testid="treatments-category-filter">
          <option value="">All specialties</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(t => (
          <Link to={`/treatments/${t.slug}`} key={t.id} className="mp-card p-7 block" data-testid={`treatment-card-${t.slug}`}>
            <span className="text-xs font-mono uppercase tracking-widest text-[#B47A9A]">{t.category}</span>
            <h3 className="font-serif text-2xl text-[#F3E9EC] mt-2">{t.name}</h3>
            <p className="mt-3 text-sm text-[#F3E9EC]/60 line-clamp-3">{t.overview}</p>
            <div className="mt-6 text-[#B47A9A] text-sm flex items-center gap-2">Read more <ArrowRight className="w-3.5 h-3.5"/></div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function TreatmentDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const { convertFromINR, currency } = useCountry();
  useEffect(() => { http.get(`/treatments/${slug}`).then(r => setData(r.data)); }, [slug]);
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-20 text-[#F3E9EC]/60">Loading…</div>;
  const { treatment, hospitals, doctors, costs } = data;
  const cost = costs?.[0];
  const convMin = cost ? convertFromINR(cost.min) : null;
  const convMax = cost ? convertFromINR(cost.max) : null;
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      <span className="overline">{treatment.category}</span>
      <h1 className="font-serif text-4xl md:text-6xl text-[#F3E9EC] mt-3">{treatment.name}</h1>
      <p className="mt-5 text-lg text-[#F3E9EC]/75 max-w-3xl leading-relaxed">{treatment.overview}</p>

      {cost && (
        <div className="mt-10 mp-card p-8 grid md:grid-cols-3 gap-6">
          <div>
            <div className="overline mb-2">Indicative Cost in India</div>
            <div className="font-serif text-3xl text-[#F3E9EC]">₹{cost.min.toLocaleString()} – ₹{cost.max.toLocaleString()}</div>
            {convMin != null && convMax != null && (
              <div className="mt-2 text-sm text-[#B47A9A] font-mono" data-testid="treatment-cost-converted">≈ {currency.symbol} {convMin.toLocaleString()} – {convMax.toLocaleString()} {currency.code}</div>
            )}
          </div>
          <div>
            <div className="overline mb-2">Hospital Stay</div>
            <div className="font-serif text-xl text-[#F3E9EC]">{treatment.hospital_stay}</div>
          </div>
          <div className="flex items-center">
            <Link to="/enquiry" className="btn-primary px-6 py-3 font-medium" data-testid="treatment-enquiry-cta">Get Personalised Cost</Link>
          </div>
          <p className="md:col-span-3 text-xs text-[#F3E9EC]/45">Indicative estimate only. Final treatment costs may vary depending on the patient's medical condition, doctor, hospital, investigations, length of stay and treatment requirements.</p>
        </div>
      )}

      <div className="mt-14 grid md:grid-cols-2 gap-6">
        <div className="mp-card p-7">
          <h3 className="font-serif text-2xl text-[#F3E9EC]">Who may need it</h3>
          <p className="mt-3 text-[#F3E9EC]/70 text-sm leading-relaxed">{treatment.who_may_need_it}</p>
        </div>
        <div className="mp-card p-7">
          <h3 className="font-serif text-2xl text-[#F3E9EC]">Procedure overview</h3>
          <p className="mt-3 text-[#F3E9EC]/70 text-sm leading-relaxed">{treatment.procedure_overview}</p>
        </div>
        <div className="mp-card p-7 md:col-span-2">
          <h3 className="font-serif text-2xl text-[#F3E9EC]">Recovery</h3>
          <p className="mt-3 text-[#F3E9EC]/70 text-sm leading-relaxed">{treatment.recovery}</p>
        </div>
      </div>

      {hospitals?.length > 0 && (
        <div className="mt-14">
          <h2 className="font-serif text-3xl text-[#F3E9EC] mb-6">Hospitals for this treatment</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {hospitals.map(h => (
              <Link to={`/hospitals/${h.slug}`} key={h.id} className="mp-card p-6 block">
                <div className="text-xs font-mono uppercase tracking-widest text-[#B47A9A] flex items-center gap-1"><MapPin className="w-3 h-3"/>{h.city}</div>
                <h3 className="font-serif text-xl text-[#F3E9EC] mt-2">{h.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      )}

      {doctors?.length > 0 && (
        <div className="mt-14">
          <h2 className="font-serif text-3xl text-[#F3E9EC] mb-6">Doctors for this treatment</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {doctors.map(d => (
              <Link to={`/doctors/${d.slug}`} key={d.id} className="mp-card p-6 block">
                <div className="text-xs font-mono uppercase tracking-widest text-[#B47A9A]">{d.specialty}</div>
                <h3 className="font-serif text-xl text-[#F3E9EC] mt-2">{d.name}</h3>
                <p className="text-sm text-[#F3E9EC]/60 mt-1">{d.experience_years} yrs · {d.city}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function HospitalsList() {
  const [items, setItems] = useState([]);
  useEffect(() => { http.get("/hospitals").then(r => setItems(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="overline mb-3">Hospital Directory</div>
      <h1 className="font-serif text-4xl md:text-6xl text-[#F3E9EC]">Multi-specialty hospitals in India</h1>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(h => (
          <Link to={`/hospitals/${h.slug}`} key={h.id} className="mp-card overflow-hidden block" data-testid={`hospital-card-${h.slug}`}>
            <div className="h-52"><img src={h.hero_image} alt={h.name} className="w-full h-full object-cover"/></div>
            <div className="p-6">
              <div className="flex items-center gap-2 text-xs text-[#B47A9A] font-mono uppercase tracking-widest"><MapPin className="w-3 h-3"/>{h.city}</div>
              <h3 className="font-serif text-xl text-[#F3E9EC] mt-2">{h.name}</h3>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(h.specialties||[]).slice(0,3).map(s => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full border border-[#5E3A5C]/60 text-[#F3E9EC]/75">{s}</span>)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function HospitalDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { http.get(`/hospitals/${slug}`).then(r => setData(r.data)); }, [slug]);
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-20 text-[#F3E9EC]/60">Loading…</div>;
  const { hospital, doctors, treatments } = data;
  return (
    <div>
      <div className="relative h-72 md:h-96">
        <img src={hospital.hero_image} alt={hospital.name} className="w-full h-full object-cover"/>
        <div className="absolute inset-0" style={{background: "linear-gradient(180deg, rgba(0,3,14,0.3) 0%, #00030E 100%)"}}/>
      </div>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 -mt-24 relative pb-20">
        <span className="overline flex items-center gap-2"><MapPin className="w-3 h-3"/>{hospital.city}, {hospital.state}</span>
        <h1 className="font-serif text-4xl md:text-5xl text-[#F3E9EC] mt-3">{hospital.name}</h1>
        <p className="mt-5 text-lg text-[#F3E9EC]/75 max-w-3xl">{hospital.description}</p>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          <div className="mp-card p-6">
            <div className="overline mb-3">Specialties</div>
            <div className="flex flex-wrap gap-2">{(hospital.specialties||[]).map(s => <span key={s} className="text-xs px-2.5 py-1 rounded-full border border-[#5E3A5C]/60 text-[#F3E9EC]/80">{s}</span>)}</div>
          </div>
          <div className="mp-card p-6">
            <div className="overline mb-3">Facilities</div>
            <ul className="text-sm text-[#F3E9EC]/70 space-y-1.5">{(hospital.facilities||[]).map(f => <li key={f}>• {f}</li>)}</ul>
          </div>
          <div className="mp-card p-6">
            <div className="overline mb-3">Accreditations</div>
            <div className="flex flex-wrap gap-2">{(hospital.accreditations||[]).map(a => <span key={a} className="text-xs px-2.5 py-1 rounded-full bg-[#B47A9A]/15 border border-[#B47A9A]/40 text-[#B47A9A]">{a}</span>)}</div>
          </div>
        </div>
        {doctors?.length > 0 && (
          <div className="mt-14">
            <h2 className="font-serif text-3xl text-[#F3E9EC] mb-6">Associated Doctors</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {doctors.map(d => (
                <Link to={`/doctors/${d.slug}`} key={d.id} className="mp-card p-6 block">
                  <div className="text-xs font-mono uppercase tracking-widest text-[#B47A9A]">{d.specialty}</div>
                  <h3 className="font-serif text-xl text-[#F3E9EC] mt-2">{d.name}</h3>
                  <p className="text-sm text-[#F3E9EC]/60 mt-1">{d.experience_years} yrs experience</p>
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="mt-14 mp-card p-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div><h3 className="font-serif text-2xl text-[#F3E9EC]">Interested in this hospital?</h3><p className="text-sm text-[#F3E9EC]/60 mt-1">Get a personalised treatment quote with our care coordinator.</p></div>
          <Link to="/enquiry" className="btn-primary px-6 py-3">Get Personalised Cost</Link>
        </div>
      </div>
    </div>
  );
}

export function DoctorsList() {
  const [items, setItems] = useState([]);
  useEffect(() => { http.get("/doctors").then(r => setItems(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="overline mb-3">Doctor Directory</div>
      <h1 className="font-serif text-4xl md:text-6xl text-[#F3E9EC]">Meet leading specialists in India</h1>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(d => (
          <Link to={`/doctors/${d.slug}`} key={d.id} className="mp-card p-7 block" data-testid={`doctor-card-${d.slug}`}>
            {d.photo && <div className="h-48 rounded-xl overflow-hidden mb-4"><img src={d.photo} alt={d.name} className="w-full h-full object-cover"/></div>}
            <span className="text-xs font-mono uppercase tracking-widest text-[#B47A9A]">{d.specialty}</span>
            <h3 className="font-serif text-2xl text-[#F3E9EC] mt-2">{d.name}</h3>
            <p className="text-sm text-[#F3E9EC]/60 mt-1">{d.experience_years} yrs · {d.city}</p>
            <p className="text-xs text-[#F3E9EC]/50 mt-3">{(d.qualifications||[]).join(", ")}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function DoctorDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { http.get(`/doctors/${slug}`).then(r => setData(r.data)); }, [slug]);
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-20 text-[#F3E9EC]/60">Loading…</div>;
  const { doctor, hospitals } = data;
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      <div className="grid md:grid-cols-3 gap-10">
        <div className="mp-card overflow-hidden">
          {doctor.photo && <img src={doctor.photo} alt={doctor.name} className="w-full aspect-square object-cover"/>}
        </div>
        <div className="md:col-span-2">
          <span className="overline">{doctor.specialty} · {doctor.sub_specialty}</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F3E9EC] mt-3">{doctor.name}</h1>
          <p className="mt-4 text-[#F3E9EC]/75">{doctor.experience_years} years of experience · {doctor.city}</p>
          <div className="mt-6"><div className="overline mb-2">Qualifications</div><p className="text-sm text-[#F3E9EC]/75">{(doctor.qualifications||[]).join(", ")}</p></div>
          <div className="mt-6"><div className="overline mb-2">Expertise</div><div className="flex flex-wrap gap-2">{(doctor.expertise||[]).map(e => <span key={e} className="text-xs px-2.5 py-1 rounded-full border border-[#5E3A5C]/60 text-[#F3E9EC]/80">{e}</span>)}</div></div>
          <div className="mt-6"><div className="overline mb-2">Languages</div><p className="text-sm text-[#F3E9EC]/75">{(doctor.languages||[]).join(", ")}</p></div>
          <Link to="/enquiry" className="btn-primary px-6 py-3 mt-8 inline-block">Request Consultation</Link>
        </div>
      </div>
      {hospitals?.length > 0 && (
        <div className="mt-14">
          <h2 className="font-serif text-3xl text-[#F3E9EC] mb-6">Affiliated Hospitals</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {hospitals.map(h => (
              <Link to={`/hospitals/${h.slug}`} key={h.id} className="mp-card p-6 block">
                <div className="text-xs font-mono uppercase tracking-widest text-[#B47A9A]">{h.city}</div>
                <h3 className="font-serif text-xl text-[#F3E9EC] mt-2">{h.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CountriesList() {
  const [items, setItems] = useState([]);
  useEffect(() => { http.get("/countries").then(r => setItems(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="overline mb-3">Countries We Serve</div>
      <h1 className="font-serif text-4xl md:text-6xl text-[#F3E9EC]">Medical travel from your country</h1>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(c => (
          <Link to={`/countries/${c.slug}`} key={c.id} className="mp-card p-8 block" data-testid={`country-card-${c.slug}`}>
            <div className="text-5xl mb-4">{c.flag}</div>
            <h3 className="font-serif text-2xl text-[#F3E9EC]">{c.name}</h3>
            <p className="text-sm text-[#F3E9EC]/60 mt-2">Currency: {c.currency} · {c.flight_hours}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function CountryDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { http.get(`/countries/${slug}`).then(r => setData(r.data)); }, [slug]);
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-20 text-[#F3E9EC]/60">Loading…</div>;
  const { country, treatments } = data;
  const sections = (country.sections || []).filter(s => s.visible).sort((a,b) => a.order - b.order);
  const render = (k) => {
    if (k === "hero") return (
      <section className="hero-glow py-24" key={k}>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-5xl mb-4">{country.flag}</div>
            <h1 className="font-serif text-4xl md:text-6xl text-[#F3E9EC]">{country.hero_title}</h1>
            <p className="mt-5 text-[#F3E9EC]/75 text-lg">{country.hero_subtitle}</p>
            <div className="mt-8 flex gap-4"><Link to="/enquiry" className="btn-primary px-7 py-4 font-medium">Get Personalised Cost</Link></div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-[#5E3A5C]/40"><img src={country.hero_image} alt={country.name} className="w-full h-full object-cover"/></div>
        </div>
      </section>);
    if (k === "featured_treatments") return (
      <section key={k} className="py-20 max-w-7xl mx-auto px-6">
        <h2 className="font-serif text-3xl md:text-5xl text-[#F3E9EC] mb-10">Popular Treatments for {country.name}</h2>
        <div className="grid md:grid-cols-3 gap-6">{treatments.map(t => (
          <Link to={`/treatments/${t.slug}`} key={t.id} className="mp-card p-7 block">
            <span className="text-xs font-mono uppercase tracking-widest text-[#B47A9A]">{t.category}</span>
            <h3 className="font-serif text-xl text-[#F3E9EC] mt-2">{t.name}</h3>
          </Link>))}</div>
      </section>);
    if (k === "travel_visa") return (
      <section key={k} className="plum-section py-20"><div className="max-w-4xl mx-auto px-6">
        <h2 className="font-serif text-3xl md:text-4xl text-[#F3E9EC]">Travel & Visa from {country.name}</h2>
        <div className="mt-6 mp-card p-6"><div className="overline mb-2">Flight duration</div><p className="text-[#F3E9EC]/80">{country.flight_hours}</p></div>
        <div className="mt-4 mp-card p-6"><div className="overline mb-2">Visa</div><p className="text-[#F3E9EC]/80">{country.visa_info}</p></div>
      </div></section>);
    if (k === "faqs") return (
      <section key={k} className="py-20 max-w-4xl mx-auto px-6">
        <h2 className="font-serif text-3xl md:text-4xl text-[#F3E9EC] mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4">{(country.faqs||[]).map((f, i) => (
          <details key={i} className="mp-card p-6 group" data-testid={`faq-${i}`}>
            <summary className="font-serif text-xl text-[#F3E9EC] cursor-pointer">{f.q}</summary>
            <p className="mt-3 text-[#F3E9EC]/70 text-sm leading-relaxed">{f.a}</p>
          </details>))}</div>
      </section>);
    if (k === "cta") return (
      <section key={k} className="cream-section py-16"><div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="font-serif text-3xl md:text-5xl text-[#1A1220]">Ready to start your journey from {country.name}?</h2>
        <Link to="/enquiry" className="btn-primary px-8 py-4 mt-6 inline-block font-medium">Get Personalised Cost</Link>
      </div></section>);
    if (k === "how_it_works") return (
      <section key={k} className="py-16 max-w-6xl mx-auto px-6">
        <h2 className="font-serif text-3xl md:text-4xl text-[#F3E9EC] mb-8">Your journey from {country.name} to India</h2>
        <div className="grid md:grid-cols-3 gap-4">{["Enquire & upload reports","Receive quotes & doctor opinions","Travel & treatment coordination"].map((s,i)=>(
          <div key={i} className="mp-card p-6"><div className="font-serif text-3xl text-[#B47A9A]">0{i+1}</div><p className="mt-3 text-[#F3E9EC]/80">{s}</p></div>
        ))}</div></section>);
    if (k === "cost_savings") return (
      <section key={k} className="py-16 max-w-6xl mx-auto px-6">
        <div className="mp-card p-8"><h3 className="font-serif text-2xl text-[#F3E9EC]">Transparent, indicative costs</h3>
        <p className="mt-3 text-[#F3E9EC]/70 text-sm">Use our cost calculator or request a personalised quotation for accurate estimates tailored to your medical condition.</p>
        <Link to="/costs" className="btn-outline-mauve inline-block mt-6 px-6 py-3">Open Cost Calculator</Link></div></section>);
    return null;
  };
  return <div>{sections.map(s => render(s.key))}</div>;
}
